import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DollarSign, TrendingUp, Award, Users, Trophy } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";

const MARINO = "#0a2540";
const NARANJA = "#f35b24";
const GRIS = "#a0aec0";

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-6 text-marino/60">Cargando reportes...</div>;

  const kpis = [
    { k: "Ingresos totales", v: `$${data.totals.total_revenue.toLocaleString()}`, icon: DollarSign, color: NARANJA },
    { k: "Inscripciones", v: data.totals.total_enrollments, icon: TrendingUp, color: MARINO },
    { k: "Completados", v: data.totals.total_completed, icon: Award, color: MARINO },
    { k: "Tasa de aprobación", v: `${data.totals.overall_completion_rate}%`, icon: Trophy, color: NARANJA },
  ];

  // Truncate long titles for chart labels
  const shortTitle = (t) => (t?.length > 22 ? t.slice(0, 20) + "…" : t || "");

  const roleData = Object.entries(data.role_distribution || {}).map(([name, value]) => ({ name, value }));
  const ROLE_COLORS = { student: NARANJA, instructor: MARINO, admin: GRIS };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.k} data-testid={`kpi-${k.k}`} className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm text-marino/70">{k.k}</span>
              <k.icon className="w-5 h-5" style={{ color: k.color }} />
            </div>
            <div className="font-heading font-black text-3xl text-marino mt-2">{k.v}</div>
          </div>
        ))}
      </div>

      {/* Revenue by course */}
      <div className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm">
        <h3 className="font-heading font-bold text-marino text-lg mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-naranja" /> Ingresos por curso
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.revenue_by_course.map((r) => ({ ...r, short: shortTitle(r.title) }))} margin={{ top: 5, right: 10, bottom: 40, left: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="short" tick={{ fontSize: 11, fill: MARINO }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: MARINO }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ background: MARINO, border: "none", borderRadius: 8, color: "#fff" }}
                labelStyle={{ color: "#fff" }}
                formatter={(v, name) => (name === "revenue" ? [`$${v}`, "Ingresos"] : [v, name])}
              />
              <Bar dataKey="revenue" fill={NARANJA} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Enrollments timeline */}
        <div className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm lg:col-span-2">
          <h3 className="font-heading font-bold text-marino text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-naranja" /> Inscripciones · últimos 30 días
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.enrollments_timeline} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="colorEnr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NARANJA} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={NARANJA} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: MARINO }} interval={4} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: MARINO }} />
                <Tooltip contentStyle={{ background: MARINO, border: "none", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#fff" }} />
                <Area type="monotone" dataKey="enrollments" stroke={NARANJA} strokeWidth={2} fill="url(#colorEnr)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Role distribution */}
        <div className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm">
          <h3 className="font-heading font-bold text-marino text-lg mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-naranja" /> Distribución de usuarios
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2}>
                  {roleData.map((entry) => (
                    <Cell key={entry.name} fill={ROLE_COLORS[entry.name] || GRIS} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top courses table */}
      <div className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
        <div className="p-5 border-b border-gris/20">
          <h3 className="font-heading font-bold text-marino text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-naranja" /> Ranking de cursos por ingresos
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-marino/5 text-marino/80 text-xs uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">#</th>
                <th className="p-3 text-left">Curso</th>
                <th className="p-3 text-right">Precio</th>
                <th className="p-3 text-right">Inscripciones</th>
                <th className="p-3 text-right">Completados</th>
                <th className="p-3 text-right">% aprobación</th>
                <th className="p-3 text-right">Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {data.revenue_by_course.map((r, i) => (
                <tr key={r.course_id} data-testid={`report-row-${r.course_id}`} className="border-b border-gris/10">
                  <td className="p-3 text-marino/60">{i + 1}</td>
                  <td className="p-3 text-marino font-medium">{r.title}</td>
                  <td className="p-3 text-right text-marino">${r.price}</td>
                  <td className="p-3 text-right text-marino">{r.enrollments}</td>
                  <td className="p-3 text-right text-marino">{r.completed}</td>
                  <td className="p-3 text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${r.completion_rate >= 70 ? "bg-green-100 text-green-800" : r.completion_rate >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-700"}`}>
                      {r.completion_rate}%
                    </span>
                  </td>
                  <td className="p-3 text-right text-naranja font-bold">${r.revenue}</td>
                </tr>
              ))}
              {data.revenue_by_course.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-marino/60">Sin datos aún.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
